import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  Play, 
  Pause, 
  Volume2, 
  Music, 
  Trash2, 
  Edit, 
  Download,
  Loader2,
  ShoppingBag,
  Sparkles,
  Wand2,
  ChevronLeft,
  ChevronRight,
  Layers,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import apiClient from '../utils/apiClient';

const ProductAudioManager = ({ products, voices, onProductUpdate, pagination, onPageChange, trialStatus, extendedProductLimit: extendedProductLimitProp = false }) => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [customText, setCustomText] = useState('');
  const [selectedVoice, setSelectedVoice] = useState('');
  const [generating, setGenerating] = useState(false);
  const [playingAudio, setPlayingAudio] = useState(null);
  const [audioElements, setAudioElements] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [enhanceWithClaude, setEnhanceWithClaude] = useState(false);
  const [enhancingText, setEnhancingText] = useState(false);
  const [loadingExistingText, setLoadingExistingText] = useState(false);

  // Bulk generation state
  const [bulkEnabled, setBulkEnabled] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkVoice, setBulkVoice] = useState('');
  const [bulkEnhance, setBulkEnhance] = useState(false);
  const [bulkJob, setBulkJob] = useState(null); // { job_id, status, total, processed, succeeded, failed_count, progress_pct }
  const [bulkPolling, setBulkPolling] = useState(null);

  // Feature flags from Dashboard prop
  const extendedProductLimit = extendedProductLimitProp;

  const [dualPlayerEnabled, setDualPlayerEnabled] = useState(false);
  const [metadataAudioEnabled, setMetadataAudioEnabled] = useState(false);

  // Secondary audio state
  const [secondaryDialogOpen, setSecondaryDialogOpen] = useState(false);
  const [secondaryProduct, setSecondaryProduct] = useState(null);
  const [secondaryVoice, setSecondaryVoice] = useState('');
  const [metafieldNamespace, setMetafieldNamespace] = useState('custom');
  const [metafieldKey, setMetafieldKey] = useState('');
  const [availableMetafields, setAvailableMetafields] = useState([]);
  const [loadingMetafields, setLoadingMetafields] = useState(false);
  const [generatingSecondary, setGeneratingSecondary] = useState(false);

  // Check feature flags for this shop
  useEffect(() => {
    apiClient.get('/features').then(r => {
      setBulkEnabled(r.data.features?.bulk_generation ?? false);
      setDualPlayerEnabled(r.data.features?.dual_product_player ?? false);
      setMetadataAudioEnabled(r.data.features?.metadata_audio ?? false);
      if (voices.length > 0) setBulkVoice(voices[0]?.voice_id || '');
    }).catch(() => {});
  }, []);

  const openSecondaryDialog = async (product) => {
    setSecondaryProduct(product);
    setSecondaryVoice(voices[0]?.voice_id || '');
    setMetafieldNamespace('custom');
    setMetafieldKey('');
    setAvailableMetafields([]);
    setSecondaryDialogOpen(true);

    if (metadataAudioEnabled) {
      setLoadingMetafields(true);
      try {
        const res = await apiClient.get(`/products/${product.id}/metafields`);
        setAvailableMetafields(res.data.metafields || []);
      } catch (e) { /* silent */ }
      finally { setLoadingMetafields(false); }
    }
  };

  const handleGenerateSecondaryAudio = async () => {
    if (!secondaryProduct || !secondaryVoice) {
      toast.error('Please select a voice');
      return;
    }
    setGeneratingSecondary(true);
    try {
      await apiClient.post('/product-audio', {
        product_id: secondaryProduct.id,
        voice_id: secondaryVoice,
        audio_slot: 'secondary',
        metafield_namespace: metafieldKey ? metafieldNamespace : null,
        metafield_key: metafieldKey || null,
      });
      toast.success('Secondary audio generated!');
      setSecondaryDialogOpen(false);
      onProductUpdate && onProductUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate secondary audio');
    } finally {
      setGeneratingSecondary(false);
    }
  };

  // Poll bulk job status every 3 seconds while in_progress / queued
  useEffect(() => {
    if (!bulkJob?.job_id || bulkJob.status === 'completed' || bulkJob.status === 'failed') {
      if (bulkPolling) { clearInterval(bulkPolling); setBulkPolling(null); }
      return;
    }
    const interval = setInterval(async () => {
      try {
        const r = await apiClient.get(`/audio/bulk-status/${bulkJob.job_id}`);
        setBulkJob(r.data);
        if (r.data.status === 'completed') {
          toast.success(`Bulk generation complete — ${r.data.succeeded} products done!`);
          onProductUpdate && onProductUpdate();
        }
      } catch (e) { /* silent */ }
    }, 3000);
    setBulkPolling(interval);
    return () => clearInterval(interval);
  }, [bulkJob?.job_id, bulkJob?.status]);

  // Check if trial limits reached — bypassed when extended_product_limit flag is on
  const isTrialLimitReached = !extendedProductLimit && trialStatus?.trial_active &&
    trialStatus?.full_trial?.products_remaining === 0;

  const isTrialExpired = !extendedProductLimit && trialStatus?.trial_active &&
    trialStatus?.full_trial?.expired;

  const handleEnhanceText = async () => {
    if (!customText.trim() && !selectedProduct?.description) {
      toast.error('No text to enhance');
      return;
    }

    setEnhancingText(true);
    
    try {
      const textToEnhance = customText.trim() || selectedProduct.description;
      const response = await apiClient.post('/enhance-text', {
        text: textToEnhance,
        enhancement_type: 'improve'
      });

      setCustomText(response.data.enhanced_text);
      toast.success('Text enhanced with Claude AI!');
      
    } catch (error) {
      console.error('Error enhancing text:', error);
      toast.error('Failed to enhance text');
    } finally {
      setEnhancingText(false);
    }
  };

  const handleGenerateAudio = async () => {
    if (!selectedProduct || !selectedVoice) {
      toast.error('Please select a product and voice');
      return;
    }

    setGenerating(true);
    
    try {
      const response = await apiClient.post('/product-audio', {
        product_id: selectedProduct.id,
        custom_text: customText.trim() || null,
        voice_id: selectedVoice,
        enhance_with_claude: enhanceWithClaude
      });

      toast.success('Audio generated successfully!');
      setDialogOpen(false);
      setCustomText('');
      setSelectedProduct(null);
      setSelectedVoice('');
      setEnhanceWithClaude(false);
      onProductUpdate();
      
    } catch (error) {
      console.error('Error generating audio:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate audio');
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteAudio = async (productId) => {
    try {
      await apiClient.delete(`/product-audio/${productId}`);
      toast.success('Audio deleted successfully');
      onProductUpdate();
    } catch (error) {
      console.error('Error deleting audio:', error);
      toast.error('Failed to delete audio');
    }
  };

  const handlePlayAudio = (productId, audioUrl) => {
    // Stop any currently playing audio
    if (playingAudio && audioElements[playingAudio]) {
      audioElements[playingAudio].pause();
      audioElements[playingAudio].currentTime = 0;
    }

    // If clicking the same audio that's playing, just stop it
    if (playingAudio === productId) {
      setPlayingAudio(null);
      return;
    }

    // Create new audio element if it doesn't exist
    if (!audioElements[productId]) {
      const audio = new Audio(audioUrl);
      audio.onended = () => setPlayingAudio(null);
      audio.onerror = () => {
        toast.error('Failed to play audio');
        setPlayingAudio(null);
      };
      setAudioElements(prev => ({ ...prev, [productId]: audio }));
      
      audio.play();
      setPlayingAudio(productId);
    } else {
      audioElements[productId].play();
      setPlayingAudio(productId);
    }
  };

  const handlePauseAudio = (productId) => {
    if (audioElements[productId]) {
      audioElements[productId].pause();
      setPlayingAudio(null);
    }
  };

  const handleBulkGenerate = async () => {
    if (!bulkVoice) { toast.error('Please select a voice'); return; }
    const productIds = products.filter(p => !p.has_audio).map(p => p.id);
    if (productIds.length === 0) { toast.info('All products already have audio'); return; }
    try {
      const r = await apiClient.post('/audio/bulk-generate', {
        product_ids: productIds,
        voice_id: bulkVoice,
        enhance_with_claude: bulkEnhance,
      });
      setBulkJob({ job_id: r.data.job_id, status: 'queued', total: r.data.total, processed: 0, succeeded: 0, failed_count: 0, progress_pct: 0 });
      setBulkDialogOpen(false);
      toast.success(`Bulk job started for ${r.data.total} products`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start bulk generation');
    }
  };

  const openGenerateDialog = async (product) => {
    setSelectedProduct(product);
    setCustomText('');
    setSelectedVoice(voices[0]?.voice_id || '');
    setEnhanceWithClaude(false);
    setDialogOpen(true);

    // Pre-fill saved text_content and voice when editing existing audio
    if (product.has_audio) {
      setLoadingExistingText(true);
      try {
        const response = await apiClient.get(`/product-audio/${product.id}`);
        setCustomText(response.data.text_content || '');
        if (response.data.voice_id) {
          setSelectedVoice(response.data.voice_id);
        }
      } catch (error) {
        console.error('Could not load existing audio text:', error);
        // Non-blocking — merchant can still type new text
      } finally {
        setLoadingExistingText(false);
      }
    }
  };

  const truncateText = (text, maxLength = 100) => {
    const cleanText = text.replace(/<[^>]*>/g, '').trim();
    return cleanText.length > maxLength ? cleanText.substring(0, maxLength) + '...' : cleanText;
  };

  return (
    <div className="space-y-6">
      {/* Extended Product Limit Badge */}
      {extendedProductLimit && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Sparkles className="h-5 w-5 text-indigo-600" />
            <div>
              <p className="text-sm font-semibold text-indigo-900">Unlimited Products (Custom Plan)</p>
              <p className="text-xs text-indigo-700">Your plan has no product limit — generate audio for as many products as you need.</p>
            </div>
            <Badge className="bg-indigo-600 text-white ml-auto">Unlimited</Badge>
          </div>
        </div>
      )}

      {/* Trial Info Banner — hidden when extended_product_limit is active */}
      {!extendedProductLimit && trialStatus?.trial_active && !trialStatus?.full_trial?.expired && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sparkles className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-sm font-semibold text-blue-900">
                  Trial: {trialStatus.full_trial.products_generated}/{trialStatus.full_trial.products_limit} products used
                </p>
                <p className="text-xs text-blue-700">
                  {trialStatus.full_trial.products_remaining} products remaining
                </p>
              </div>
            </div>
            {trialStatus.full_trial.products_remaining <= 2 && (
              <Badge className="bg-orange-500 text-white">
                Almost at limit!
              </Badge>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Product Audio Management</h3>
          <p className="text-sm text-gray-600">Generate and manage audio descriptions for your products</p>
        </div>
        {bulkEnabled && (
          <Button
            data-testid="bulk-generate-btn"
            variant="outline"
            className="flex items-center gap-2 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            onClick={() => setBulkDialogOpen(true)}
          >
            <Layers className="h-4 w-4" />
            Bulk Generate
          </Button>
        )}
      </div>

      {/* Bulk job progress bar */}
      {bulkJob && (
        <div data-testid="bulk-progress" className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-indigo-800">
              {bulkJob.status === 'completed' ? 'Bulk generation complete' : 'Bulk generation in progress…'}
            </span>
            <span className="text-indigo-600">{bulkJob.processed}/{bulkJob.total}</span>
          </div>
          <Progress value={bulkJob.progress_pct} className="h-2" />
          <div className="flex gap-4 text-xs text-indigo-700">
            <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-green-600" />{bulkJob.succeeded} done</span>
            {bulkJob.failed_count > 0 && <span className="flex items-center gap-1"><XCircle className="h-3 w-3 text-red-500" />{bulkJob.failed_count} failed</span>}
          </div>
        </div>
      )}

      {/* Bulk Generate Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Layers className="h-5 w-5 text-indigo-600" />Bulk Generate Audio</DialogTitle>
            <DialogDescription>
              Generate audio for all {products.filter(p => !p.has_audio).length} products that don't have audio yet.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Voice</label>
              <Select value={bulkVoice} onValueChange={setBulkVoice}>
                <SelectTrigger data-testid="bulk-voice-select"><SelectValue placeholder="Choose a voice" /></SelectTrigger>
                <SelectContent>
                  {voices.map(v => (
                    <SelectItem key={v.voice_id} value={v.voice_id}>{v.name}{v.is_custom ? ' (Custom)' : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-purple-50 border border-purple-200">
              <div>
                <p className="text-sm font-medium text-purple-900">Enhance with Claude</p>
                <p className="text-xs text-purple-700">AI-optimises each product description before generating</p>
              </div>
              <Switch checked={bulkEnhance} onCheckedChange={setBulkEnhance} data-testid="bulk-enhance-switch" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setBulkDialogOpen(false)}>Cancel</Button>
              <Button
                data-testid="bulk-confirm-btn"
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                onClick={handleBulkGenerate}
              >
                Start Bulk Generation
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.map((product) => (
          <Card key={product.id} className="bg-white border border-gray-200 hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-base font-semibold text-gray-900 mb-1 line-clamp-2">
                    {product.title}
                  </CardTitle>
                  <div className="flex items-center space-x-2">
                    {product.has_audio ? (
                      <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">
                        <Music className="h-3 w-3 mr-1" />
                        Has Audio
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-gray-600 border-gray-300">
                        No Audio
                      </Badge>
                    )}
                  </div>
                </div>
                {product.image && (
                  <Avatar className="h-12 w-12 ml-3">
                    <AvatarImage src={product.image} alt={product.title} />
                    <AvatarFallback>
                      <ShoppingBag className="h-6 w-6" />
                    </AvatarFallback>
                  </Avatar>
                )}
              </div>
            </CardHeader>
            
            <CardContent className="pt-0">
              <div className="space-y-3">
                {product.description && (
                  <p className="text-sm text-gray-600 line-clamp-3">
                    {truncateText(product.description)}
                  </p>
                )}
                
                <div className="flex items-center justify-between pt-2">
                  {product.has_audio ? (
                    <div className="flex items-center space-x-2 flex-wrap gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          playingAudio === product.id
                            ? handlePauseAudio(product.id)
                            : handlePlayAudio(product.id, product.audio_url)
                        }
                        data-testid={`play-audio-${product.id}`}
                      >
                        {playingAudio === product.id ? (
                          <Pause className="h-4 w-4" />
                        ) : (
                          <Play className="h-4 w-4" />
                        )}
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openGenerateDialog(product)}
                        data-testid={`edit-audio-${product.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteAudio(product.id)}
                        className="text-red-600 hover:text-red-700 hover:border-red-300"
                        data-testid={`delete-audio-${product.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>

                      {dualPlayerEnabled && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openSecondaryDialog(product)}
                          className="text-purple-600 hover:text-purple-700 hover:border-purple-300 text-xs"
                          data-testid={`secondary-audio-${product.id}`}
                          title="Generate secondary audio (metadata/brand story)"
                        >
                          +2nd
                        </Button>
                      )}
                    </div>
                  ) : (
                    <Button
                      onClick={() => openGenerateDialog(product)}
                      size="sm"
                      className="bg-indigo-600 hover:bg-indigo-700 text-white"
                      data-testid={`generate-audio-${product.id}`}
                      disabled={isTrialLimitReached || isTrialExpired}
                    >
                      <Volume2 className="h-4 w-4 mr-2" />
                      {isTrialLimitReached ? 'Trial Limit Reached' : isTrialExpired ? 'Trial Expired' : 'Generate Audio'}
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {products.length === 0 && (
        <div className="text-center py-12">
          <ShoppingBag className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Products Found</h3>
          <p className="text-gray-600">Make sure your Shopify store has products to manage audio for.</p>
        </div>
      )}

      {/* Pagination Controls */}
      {pagination && pagination.total_pages > 1 && (
        <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 rounded-lg mt-6">
          <div className="flex flex-1 justify-between sm:hidden">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange && onPageChange(pagination.page - 1)}
              disabled={!pagination.has_prev}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange && onPageChange(pagination.page + 1)}
              disabled={!pagination.has_next}
            >
              Next
            </Button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span> to{' '}
                <span className="font-medium">
                  {Math.min(pagination.page * pagination.limit, pagination.total)}
                </span>{' '}
                of <span className="font-medium">{pagination.total}</span> products
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPageChange && onPageChange(pagination.page - 1)}
                  disabled={!pagination.has_prev}
                  className="relative inline-flex items-center rounded-l-md"
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="h-4 w-4" />
                  <span className="ml-1">Previous</span>
                </Button>
                
                {/* Page Numbers */}
                <div className="flex items-center px-3 text-sm font-medium text-gray-700">
                  Page {pagination.page} of {pagination.total_pages}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPageChange && onPageChange(pagination.page + 1)}
                  disabled={!pagination.has_next}
                  className="relative inline-flex items-center rounded-r-md"
                  data-testid="next-page-btn"
                >
                  <span className="mr-1">Next</span>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Secondary Audio Dialog */}
      <Dialog open={secondaryDialogOpen} onOpenChange={setSecondaryDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Layers className="h-5 w-5 text-purple-600" />
              <span>Generate Secondary Audio</span>
            </DialogTitle>
            <DialogDescription>
              {secondaryProduct && (
                <span>Add a second audio track for: <strong>{secondaryProduct.title}</strong></span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Voice</label>
              <Select value={secondaryVoice} onValueChange={setSecondaryVoice}>
                <SelectTrigger><SelectValue placeholder="Choose a voice" /></SelectTrigger>
                <SelectContent>
                  {voices.map(v => (
                    <SelectItem key={v.voice_id} value={v.voice_id}>{v.name}{v.is_custom ? ' (Custom)' : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {metadataAudioEnabled && (
              <div className="space-y-3 bg-purple-50 border border-purple-200 rounded-lg p-4">
                <p className="text-sm font-medium text-purple-900">Metadata Source (Optional)</p>
                <p className="text-xs text-purple-700">Use a custom metafield as the audio text instead of the product description.</p>

                {loadingMetafields ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading metafields…
                  </div>
                ) : availableMetafields.length > 0 ? (
                  <Select
                    value={metafieldKey ? `${metafieldNamespace}.${metafieldKey}` : ''}
                    onValueChange={(val) => {
                      if (!val) { setMetafieldNamespace('custom'); setMetafieldKey(''); return; }
                      const [ns, ...rest] = val.split('.');
                      setMetafieldNamespace(ns);
                      setMetafieldKey(rest.join('.'));
                    }}
                  >
                    <SelectTrigger><SelectValue placeholder="Use product description (default)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Use product description (default)</SelectItem>
                      {availableMetafields.map(mf => (
                        <SelectItem key={`${mf.namespace}.${mf.key}`} value={`${mf.namespace}.${mf.key}`}>
                          {mf.namespace}.{mf.key} ({mf.type})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="flex gap-2">
                    <input
                      className="flex-1 border rounded px-2 py-1 text-sm"
                      placeholder="Namespace (e.g. custom)"
                      value={metafieldNamespace}
                      onChange={e => setMetafieldNamespace(e.target.value)}
                    />
                    <input
                      className="flex-1 border rounded px-2 py-1 text-sm"
                      placeholder="Key (e.g. brand_story)"
                      value={metafieldKey}
                      onChange={e => setMetafieldKey(e.target.value)}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex justify-end space-x-3 pt-2 border-t">
            <Button variant="outline" onClick={() => setSecondaryDialogOpen(false)} disabled={generatingSecondary}>Cancel</Button>
            <Button
              onClick={handleGenerateSecondaryAudio}
              disabled={generatingSecondary || !secondaryVoice}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {generatingSecondary ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating…</> : <><Volume2 className="h-4 w-4 mr-2" />Generate Secondary Audio</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Generate Audio Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center space-x-2">
              <Volume2 className="h-5 w-5 text-indigo-600" />
              <span>Generate Product Audio</span>
            </DialogTitle>
            <DialogDescription>
              {selectedProduct && (
                <span>Create audio description for: <strong>{selectedProduct.title}</strong></span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 overflow-y-auto flex-1">
            {/* Voice Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Select Voice</label>
              <Select value={selectedVoice} onValueChange={setSelectedVoice}>
                <SelectTrigger data-testid="voice-select">
                  <SelectValue placeholder="Choose a voice" />
                </SelectTrigger>
                <SelectContent>
                  {voices.map((voice) => (
                    <SelectItem key={voice.voice_id} value={voice.voice_id}>
                      <div className="flex items-center space-x-2">
                        <span>{voice.name}</span>
                        <Badge variant="outline" className="ml-2 text-xs">
                          {voice.category}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Claude Enhancement Toggle */}
            <div className="space-y-3 bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Sparkles className="h-5 w-5 text-purple-600" />
                  <label className="text-sm font-medium text-gray-700">Enhance with Claude AI</label>
                </div>
                <Switch
                  checked={enhanceWithClaude}
                  onCheckedChange={setEnhanceWithClaude}
                  data-testid="claude-enhance-toggle"
                />
              </div>
              <p className="text-xs text-gray-600">
                Let Claude AI automatically improve and optimize your product description for better audio narration.
              </p>
            </div>

            {/* Custom Text */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Custom Text (Optional)</label>
                {customText.trim() || selectedProduct?.description ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleEnhanceText}
                    disabled={enhancingText || loadingExistingText}
                    className="text-purple-600 border-purple-200 hover:bg-purple-50"
                  >
                    {enhancingText ? (
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    ) : (
                      <Wand2 className="h-3 w-3 mr-1" />
                    )}
                    Enhance with Claude
                  </Button>
                ) : null}
              </div>
              {loadingExistingText ? (
                <div className="flex items-center justify-center h-[120px] rounded-md border border-input bg-background">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading saved text...
                  </div>
                </div>
              ) : (
                <Textarea
                  data-testid="custom-text-input"
                  placeholder="Leave empty to use product description, or enter custom text..."
                  value={customText}
                  onChange={(e) => setCustomText(e.target.value)}
                  rows={8}
                  className="resize-none min-h-[120px] max-h-[300px]"
                />
              )}
              <p className="text-xs text-gray-500">
                If left empty, the product description will be used for audio generation.
              </p>
            </div>

            {/* Preview of text that will be used */}
            {selectedProduct && (
              <div className="bg-gray-50 p-3 rounded-lg max-h-[200px] overflow-y-auto">
                <p className="text-xs font-medium text-gray-700 mb-1">Text to be converted:</p>
                <p className="text-sm text-gray-600 whitespace-pre-wrap">
                  {customText.trim() || truncateText(selectedProduct.description || selectedProduct.title, 500)}
                </p>
              </div>
            )}
          </div>
          
          <div className="flex justify-end space-x-3 pt-4 border-t flex-shrink-0">
            <Button 
              variant="outline" 
              onClick={() => setDialogOpen(false)}
              disabled={generating}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleGenerateAudio}
              disabled={generating || !selectedVoice || loadingExistingText}
              data-testid="confirm-generate-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Volume2 className="h-4 w-4 mr-2" />
                  Generate Audio
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductAudioManager;
